import type { ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import { BadgeIndianRupee, HandCoins } from 'lucide-react';
import type { PayrollRun, PayrollRunRow } from '@tailoring-crm/shared';
import { Button } from '@tailoring-crm/ui';
import { formatCurrency, formatNumber } from '../../lib/format.js';

/**
 * One person's line per row: what they made, what they drew, what was added on, what is left.
 *
 * NOT ONE FIGURE ON THIS TABLE IS WORKED OUT HERE. Every money cell is a field the server sent,
 * in integer paise, handed straight to `formatCurrency` — including the totals row, which reads
 * the run's own totals rather than adding the column up on screen. A shop that is shown one
 * number by the run endpoint and a different one by the table it renders has no way to tell
 * which of the two it is about to pay.
 *
 * `payablePaise` is SIGNED and is rendered as it arrives. Somebody who drew more in advances
 * than the cycle earned shows a negative figure, because that is what carries into the next
 * cycle; clamping it to zero on screen would quietly forgive the difference.
 */
export interface PayrollRunTableProps {
  run: PayrollRun;
  /** Owner-only. A manager reads the table and acts on none of it. */
  canWrite: boolean;
  onRecordAdvance: (row: PayrollRunRow) => void;
  onAddAddOn: (row: PayrollRunRow) => void;
}

export function PayrollRunTable({
  run,
  canWrite,
  onRecordAdvance,
  onAddAddOn,
}: PayrollRunTableProps): ReactElement {
  const { t } = useTranslation();

  return (
    <div className="overflow-x-auto rounded-lg border border-border">
      <table className="w-full min-w-[46rem] text-sm">
        <caption className="sr-only">{t('payroll.tableCaption')}</caption>
        <thead className="bg-muted/50 text-left">
          <tr>
            <th scope="col" className="px-3 py-2 font-medium">
              {t('payroll.columns.employee')}
            </th>
            <th scope="col" className="px-3 py-2 text-right font-medium">
              {t('payroll.columns.piecesDone')}
            </th>
            <th scope="col" className="px-3 py-2 text-right font-medium">
              {t('payroll.columns.totalSalary')}
            </th>
            <th scope="col" className="px-3 py-2 text-right font-medium">
              {t('payroll.columns.advance')}
            </th>
            <th scope="col" className="px-3 py-2 text-right font-medium">
              {t('payroll.columns.addOn')}
            </th>
            <th scope="col" className="px-3 py-2 text-right font-medium">
              {t('payroll.columns.payable')}
            </th>
          </tr>
        </thead>

        <tbody>
          {run.rows.map((row) => (
            <tr key={row.staffUserId} className="border-t border-border align-top">
              <th scope="row" className="px-3 py-2 text-left font-medium">
                <span className="block">{row.staffName}</span>
                {canWrite ? (
                  <span className="mt-1 flex flex-wrap gap-1">
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={() => onRecordAdvance(row)}
                    >
                      <HandCoins aria-hidden="true" />
                      {t('payroll.recordAdvance')}
                    </Button>
                    <Button type="button" variant="ghost" size="sm" onClick={() => onAddAddOn(row)}>
                      <BadgeIndianRupee aria-hidden="true" />
                      {t('payroll.addAddOn')}
                    </Button>
                  </span>
                ) : null}
              </th>
              <td className="px-3 py-2 text-right tabular-nums">{formatNumber(row.piecesDone)}</td>
              <td className="px-3 py-2 text-right tabular-nums">
                {formatCurrency(row.totalSalaryPaise)}
              </td>
              <td className="px-3 py-2 text-right tabular-nums">
                {formatCurrency(row.advancePaise)}
              </td>
              <td className="px-3 py-2 text-right tabular-nums">
                {formatCurrency(row.addOnPaise)}
              </td>
              <td className="px-3 py-2 text-right font-semibold tabular-nums">
                {formatCurrency(row.payablePaise)}
              </td>
            </tr>
          ))}
        </tbody>

        {/* The run's own totals. Never the column, added up again on screen. */}
        <tfoot className="border-t border-border bg-muted/30">
          <tr>
            <th scope="row" className="px-3 py-2 text-left font-semibold">
              {t('payroll.totalsRow')}
            </th>
            <td className="px-3 py-2" />
            <td className="px-3 py-2 text-right font-semibold tabular-nums">
              {formatCurrency(run.totals.totalSalaryPaise)}
            </td>
            <td className="px-3 py-2 text-right font-semibold tabular-nums">
              {formatCurrency(run.totals.advancePaise)}
            </td>
            <td className="px-3 py-2 text-right font-semibold tabular-nums">
              {formatCurrency(run.totals.addOnPaise)}
            </td>
            <td className="px-3 py-2 text-right font-semibold tabular-nums">
              {formatCurrency(run.totals.payablePaise)}
            </td>
          </tr>
        </tfoot>
      </table>
    </div>
  );
}
